import { IconUsersGroup, IconVideo } from "@tabler/icons-react"
import { type FC, type ReactNode, useState } from "react"
import { useNavigate } from "react-router"

import { type ThinBokaderoCourse } from "@forento/core/models/bokadero"
import { cn } from "@forento/ui/utilities/tailwind"

import CourseStatus from "~/components/course-status"
import Layout from "~/components/layout"
import Loader from "~/components/loader"
import { query } from "~/utilities/trpc"

import flagSweden from "~/assets/flag-sweden.svg"

const steps = [
	"Ditt Forento-konto och din akademi skapas med dina Bokadero-uppgifter",
	"Dina valda kurser kopieras automatiskt till din nya akademi",
	"Du ser en bekräftelse och loggar in på Forento med samma e-post och lösenord som här",
	"Du kan anpassa dina kurser, använda erbjudandet om 50% rabatt på Scale och börja sälja",
]

const BuildYourOwnPage: FC = () => {
	const navigate = useNavigate()
	const [selectedCourses, setSelectedCourses] = useState<Set<string>>(new Set())
	const [createError, setCreateError] = useState<string | null>(null)

	const { data: courses, error } = query.listCourses.useQuery()

	const createAcademy = query.createAcademy.useMutation({
		onSuccess: data => {
			if (data.status === "success") {
				navigate("/build-your-own/success", { state: { signInUrl: data.signInUrl } })
				return
			}
			if (data.status === "email-taken") {
				setCreateError(
					"Det finns redan ett Forento-konto med denna e-postadress. Logga in på app.forento.io och kontakta support om du behöver hjälp.",
				)
			}
		},
		onError: () => {
			setCreateError("Något gick fel. Försök igen eller kontakta support.")
		},
	})

	function toggleCourseSelection(courseId: string) {
		setSelectedCourses(prev => {
			const newSet = new Set(prev)
			if (newSet.has(courseId)) newSet.delete(courseId)
			else newSet.add(courseId)
			return newSet
		})
	}

	function handleCreateAcademy() {
		setCreateError(null)
		createAcademy.mutate({ courseIds: Array.from(selectedCourses) })
	}

	return (
		<Layout>
			<div className="mx-auto flex w-full max-w-6xl flex-col gap-8 py-10">
				<div>
					<h1 className="text-3xl font-bold text-slate-900">Bygg din egna akademi</h1>
					<p className="mt-4 text-slate-600">
						I samarbete med den moderna kursplattformen Forento kan du enkelt skapa din egna professionella
						utbildningsplattform. Välj vilka kurser du vill ha med så skapar vi en komplett akademi åt dig.
					</p>
					<p className="mt-4 text-slate-600">
						Du kan anpassa din akademi, lägga till fler kurser, videor, coachingsessioner, webinar,
						evenemang, och interagera med din community. Du kan även ha din akademi på svenska.
					</p>
				</div>

				<div className="grid grid-cols-1 gap-6 md:grid-cols-3">
					<BenefitCard
						icon={<IconVideo className="size-12" />}
						title="Fler format"
						description="Lägg till videor, coachingsessioner, webinar och evenemang för en rikare utbildningsupplevelse"
					/>
					<BenefitCard
						icon={<IconUsersGroup className="size-12" />}
						title="Community & interaktion"
						description="Bygg en engagerad community där studenter kan interagera, diskutera och lära tillsammans"
					/>
					<BenefitCard
						icon={<img src={flagSweden} alt="Svensk flagga" className="size-12" />}
						title="Stöd för svenska"
						description="Ha din akademi helt på svenska med fullständigt språkstöd för dina svenska kunder"
					/>
				</div>

				<div className="relative rounded-xl border-2 border-orange-300 bg-linear-to-br from-orange-50 via-yellow-50 to-orange-100 p-8 text-center shadow-xl">
					<div className="absolute -top-2 -right-2 rotate-12 transform rounded-full bg-red-500 px-4 py-1 text-sm font-bold text-white shadow-lg">
						50% RABATT!
					</div>

					<div className="mb-4 flex items-center justify-center gap-3">
						<div className="text-3xl">🎉</div>
						<div>
							<h2 className="text-2xl font-bold text-orange-800">Specialerbjudande!</h2>
							<p className="text-lg font-semibold text-orange-700">
								Få 50% rabatt på Forento Scale-planen för Bokaderomedlemmar
							</p>
						</div>
						<div className="text-3xl">🎉</div>
					</div>

					<p className="font-medium text-orange-600">
						✨ Kopieringen av dina kurser till Forento sker automatiskt av oss
					</p>
				</div>

				<div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
					<div className="mb-6">
						<h2 className="text-2xl font-semibold text-slate-900">Välj kurser att kopiera</h2>
						<p className="mt-2 text-slate-600">
							Markera de kurser du vill inkludera i din nya akademi. Du kan alltid lägga till fler senare.
						</p>
					</div>

					{error ? (
						<div className="flex flex-col items-center justify-center py-10">
							<span className="text-lg font-medium text-slate-600">Kunde inte ladda kurser</span>
						</div>
					) : courses === undefined ? (
						<Loader label="Laddar kurser..." />
					) : (
						<>
							<div className="mb-6 flex items-center justify-between">
								<span className="text-sm text-slate-500">
									{selectedCourses.size} av {courses.length} kurser valda
								</span>
								<button
									type="button"
									onClick={() => setSelectedCourses(new Set(courses.map(c => c.id)))}
									className="text-primary text-sm font-medium hover:underline"
								>
									Välj alla
								</button>
							</div>

							<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
								{courses.map(course => (
									<SelectableCourse
										key={course.id}
										course={course}
										isSelected={selectedCourses.has(course.id)}
										onToggle={() => toggleCourseSelection(course.id)}
									/>
								))}
							</div>
						</>
					)}
				</div>

				<div className="border-primary/20 from-primary/5 to-primary/10 rounded-xl border bg-linear-to-r p-8 text-center">
					<h3 className="text-2xl font-semibold text-slate-900">Redo att skapa din akademi?</h3>
					<p className="mt-2 text-slate-600">
						{selectedCourses.size > 0
							? `Du har valt ${selectedCourses.size} ${selectedCourses.size === 1 ? "kurs" : "kurser"} att kopiera till din nya akademi.`
							: "Välj kurser ovan för att komma igång, eller skapa en tom akademi som du kan fylla senare."}
					</p>
					{createError !== null && (
						<p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
							{createError}
						</p>
					)}
					<button
						type="button"
						onClick={handleCreateAcademy}
						disabled={createAcademy.isPending}
						className="bg-primary hover:bg-primary/90 mt-6 cursor-pointer rounded-lg px-8 py-3 text-lg font-semibold text-white shadow-lg transition hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
					>
						{createAcademy.isPending ? "Skapar…" : "Skapa min akademi på Forento"}
					</button>
					<p className="mt-3 text-sm text-slate-500">
						När det är klart visar vi en bekräftelse och du kan logga in på Forento med samma uppgifter som
						här
					</p>
				</div>

				<div className="rounded-xl bg-slate-50 p-6">
					<h4 className="text-lg font-semibold text-slate-900">Vad händer härnäst?</h4>
					<div className="mt-4 space-y-3 text-sm text-slate-600">
						{steps.map((step, index) => (
							<div key={index} className="flex items-start gap-3">
								<div className="bg-primary flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white">
									{index + 1}
								</div>
								<div>{step}</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</Layout>
	)
}

type BenefitCardProps = {
	icon: ReactNode
	title: string
	description: string
}

const BenefitCard: FC<BenefitCardProps> = ({ icon, title, description }) => (
	<div className="flex flex-col items-center rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
		{icon}
		<h3 className="mt-2 text-lg font-semibold text-slate-900">{title}</h3>
		<p className="mt-2 text-slate-600">{description}</p>
	</div>
)

type SelectableCourseProps = {
	course: ThinBokaderoCourse
	isSelected: boolean
	onToggle: () => void
}

const SelectableCourse: FC<SelectableCourseProps> = ({ course, isSelected, onToggle }) => (
	<button
		type="button"
		className={cn(
			"group relative cursor-pointer overflow-hidden rounded-xl border p-6 shadow-sm transition-all",
			isSelected
				? "border-primary bg-primary/5 shadow-md"
				: "border-slate-200 bg-white hover:-translate-y-0.5 hover:shadow-lg",
		)}
		onClick={onToggle}
	>
		<div className="absolute top-4 right-4">
			<div
				className={cn(
					"flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors",
					isSelected
						? "border-primary bg-primary text-white"
						: "group-hover:border-primary border-slate-300 bg-white",
				)}
			>
				{isSelected && <span className="text-xs">✓</span>}
			</div>
		</div>

		<CourseStatus status={course.status} className="absolute top-4 left-4 shadow" />
		<Thumbnail url={course.thumbnailUrl} name={course.name} />
		<h3 className="group-hover:text-primary text-lg font-semibold text-slate-900 transition-colors">
			{course.name}
		</h3>
		<p className="mt-2 line-clamp-3 text-sm text-slate-600">{course.shortDescription}</p>
		<div className="mt-4 flex items-center justify-between">
			<span className="text-sm text-slate-400">
				{course.pagesCount} {course.pagesCount === 1 ? "sida" : "sidor"}
			</span>
			{course.price && (
				<span className="text-sm font-semibold text-slate-900">{course.price.toLocaleString("sv-SE")} kr</span>
			)}
		</div>
	</button>
)

const Thumbnail: FC<{ url: string | null; name: string }> = ({ url, name }) => (
	<div className="mb-4 flex justify-center">
		{url ? (
			<div className="flex aspect-video w-full max-w-full items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-100">
				<img src={url} alt={`Thumbnail för ${name}`} className="h-full w-full object-cover" />
			</div>
		) : (
			<div className="flex aspect-video w-full max-w-full items-center justify-center rounded border border-slate-200 bg-slate-100 text-slate-300">
				<span className="text-4xl" />
			</div>
		)}
	</div>
)

export default BuildYourOwnPage
